import { useEffect, useState } from "react";

import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import classNames from "classnames/bind";

import { Button, Drawer, Label, Loading, Message } from "@components";
import { If } from "@components/control-statements";
import { Flow } from "@containers/flows/Flow";
import { FlowAPI } from "@providers/FlowAPI";

import { ChangeHistorySidebar } from "@containers/flows/ChangeHistorySidebar";

import { FlowBox } from "@boxes/FlowBox";
import { FlowReportDrawer } from "@containers/flows/FlowReportDrawer";
import { FlowUpdateDrawer } from "@containers/flows/FlowUpdateDrawer";
import { FlowHeaderActions } from "@containers/flows/header/FlowHeaderActions";
import { FlowStorage } from "@storage/FlowStorage";
import styles from "@styles/pages/flow/Flow.module.scss";
import ROLES from "data/Roles";
import hash from "object-hash";
import { useNavigate } from "react-router-dom";
import { BoxTypes } from "shared/containers/boxesOraculo/BoxTypes";
import { Environment } from "shared/core/Environment";
import { UserStorage } from "shared/tools/UserStorage";
import { OlgaEnvironmentsAPI } from "../../providers/OlgaEnvironmentsAPI";

dayjs.extend(utc);
dayjs.extend(tz);

const css = classNames.bind(styles);

const DropDownItem = ({ title, icon, onClick, disabled }) => {
  return (
    <div
      className={css({ item: true, disabled: disabled })}
      onClick={() => onClick && onClick()}
    >
      <div className={styles.icon}>
        <i className={`fal fa-${icon}`} />
      </div>
      <span>{title}</span>
    </div>
  );
};

const FlowDetails = ({ onChange }) => {
  const [stepId, setStepId] = useState(null);
  const [loadingFlow, setLoadingFlow] = useState(true);
  const [flow, setFlow] = useState(null);
  const [steps, setSteps] = useState(null);
  const [changes, setChanges] = useState(null);
  const [updatedSteps, setUpdatedSteps] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [openReports, setOpenReports] = useState(false);
  const [openHistoric, setOpenHistoric] = useState(false);
  const [openEditFlow, setOpenEditFlow] = useState(false);
  const [updatedFlow, setUpdatedFlow] = useState(false);
  const [reports, setReports] = useState([]);
  const [folders, setFolders] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [openPublishAgain, setOpenPublishAgain] = useState(false);
  const [selectedChangeId, setSelectedChangeId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    const parts = path.split("/");
    const flowId = parts[2];

    setLoadingFlow(true);

    const load = async () => {
      const flowResponse = await FlowAPI.getFlowById(flowId);
      const stepsResponse = await FlowAPI.getStepsByFlowId(flowId);
      const lastExecutionReportResponse = await FlowAPI.getLastExecutionReport(
        flowId
      );

      const flow = flowResponse.getData(null);
      const steps = stepsResponse.getData({ boxes: [], links: [] });
      const last_execution_report = lastExecutionReportResponse.getData({
        steps: [],
      });

      FlowStorage.setFlowType(
        flow.id,
        FlowBox.isEventFlow(steps.boxes || []) ? "EVENT" : "COMMON"
      );

      const flowRestrictions = proccessFlowRestrictions({
        boxes: steps.boxes,
        flow,
      });
      flow.restrictions = flowRestrictions;

      const flowFolders = await getFolders();

      setLoadingFlow(false);
      setFlow(flow);
      setSteps(steps);
      setFolders(flowFolders);
      setSelectedChangeId(steps?.id);
      setProcessing(last_execution_report.state == "RUNNING");

      FlowStorage.resetLastFlowExecution();

      if (
        last_execution_report.state == "RUNNING" &&
        !FlowBox.isEventFlow(steps.boxes)
      ) {
        waitPreview(flow);
      } else {
        FlowStorage.setLastFlowExecution(
          lastExecutionReportResponse.getData({ steps: [] })
        );
      }
    };

    load();
  }, []);

  const loadChangesHistory = async () => {
    const response = await FlowAPI.getStepsHistorysByFlowId(flow.id);
    const data = response.getData([]);
    setChanges(data);
  };

  const getFolders = async () => {
    const environmentConfigurations =
      await OlgaEnvironmentsAPI.getConfigurations();
    const folders = environmentConfigurations.getData({})?.flow_folders || [];
    return folders;
  };

  const waitPreview = (flow) => {
    const last_execution = FlowStorage.getLastFlowExecution();
    let interval_id = null;

    const callback = async () => {
      const response = await FlowAPI.getLastExecutionReport(flow.id);
      const report = response.getData({ steps: [] });

      if (report.state == "RUNNING" || report.created_at == last_execution) {
        return;
      }

      if (report.state == "FINISHED") {
        setTimeout(async () => {
          const response = await FlowAPI.getLastExecutionReport(flow.id);
          const report = response.getData({ steps: [] });
          FlowStorage.setLastFlowExecution(report);
          setProcessing(false);
        }, 5000);
        clearInterval(interval_id);
        return;
      }

      if (report.state == "ERROR") {
        alert({
          text: "Não foi possível calcular o público",
          buttonText: "Voltar",
        });
        setProcessing(false);
        clearInterval(interval_id);
        return;
      }
    };

    interval_id = setInterval(callback.bind(this), 2000);
  };

  const preview = async () => {
    FlowStorage.resetLastFlowExecution();
    setProcessing(true);
    await FlowAPI.preview(flow.id);
    waitPreview(flow);
  };

  const onSave = async (showAlert = true) => {
    setUpdatedFlow(false);

    const newSteps = updatedSteps || steps;

    const today = dayjs();

    newSteps.boxes = newSteps.boxes.map((b) => {
      if (b.type !== "SpecificDayConditional") return b;

      return {
        ...b,
        data: {
          ...b.data,
          use_type: dayjs(b.data?.date, "DD/MM/YYYY").isSame(today, "day")
            ? "TODAY"
            : "FUTURE_DAY",
        },
      };
    });

    setLoadingFlow(true);
    const saveResponse = await FlowAPI.putStepsByFlowId(flow.id, newSteps);
    const saveNameResponse = await FlowAPI.putFlow(flow.id, {
      name: flow.name,
      folder: flow.folder,
      rank_bypass: flow.type == "RANK_BYPASS",
    });

    const saved_flow = saveNameResponse.getData({ ...flow });

    if (
      saveResponse.getErrors([]).length > 0 ||
      saveNameResponse.getErrors([]).length > 0
    ) {
      setLoadingFlow(false);
      setSteps(newSteps);
      setFlow(saved_flow);
      alert({
        text: saveResponse.getErrors(["Ocorreu um erro ao salvar"])[0],
        buttonText: "Voltar",
      });
      return;
    }

    // if (flow?.state == 'PUBLISHED') {
    // 	const publishResponse = await FlowAPI.putPublishFlow(flow.id);
    // 	if (publishResponse.getErrors([]).length > 0) {
    // 		alert({
    // 			text: publishResponse.getErrors(["Ocorreu um erro ao publicar"])[0],
    // 			buttonText: "Voltar",
    // 		});
    // 		await setStateAsync({ loadingFlow: false, steps: newSteps });
    // 		return;
    // 	}
    // }

    if (showAlert) alert({ text: "Salvo com sucesso!", buttonText: "Ok" });
    setLoadingFlow(false);
    setSteps(newSteps);
    setFlow(saved_flow);
    setUpdatedSteps(null);
    onChange && onChange(null);
  };

  const publish = async (withPriority = false) => {
    setLoadingFlow(true);
    setOpenPublishAgain(false);

    await onSave(false);

    const saveResponse = await FlowAPI.putPublishFlow(flow.id);
    const saveNameResponse = await FlowAPI.putFlow(flow.id, {
      name: flow.name,
      rank_bypass: true,
    });
    if (saveResponse.getErrors([]).length > 0) {
      alert({
        text: saveResponse.getErrors(["Ocorreu um erro ao publicar"])[0],
        buttonText: "Voltar",
      });
    } else if (saveNameResponse.getErrors([]).length > 0) {
      alert({
        text: saveNameResponse.getErrors(["Ocorreu um erro ao salvar"])[0],
        buttonText: "Voltar",
      });
    } else {
      alert({ text: "Publicado com sucesso!", buttonText: "Ok" });
    }
    const flowResponse = await FlowAPI.getFlowById(flow.id);
    setLoadingFlow(false);
    setFlow(flowResponse.getData(null));
  };

  const selectChangeMoment = async (change) => {
    if (change) {
      const { id, boxes, links } = change;

      setStepId(id);
      setSelectedChangeId(id);
      setSteps({ id, boxes, links });
    }
  };

  const loadReports = async () => {
    const response = await FlowAPI.getFlowReports(flow.id, stepId);
    const data = response.getData({});
    setOpenReports(true);
    setReports(data);
  };

  const loadHistoric = async () => {
    setOpenHistoric(true);
    setCollapsed(false);
    loadChangesHistory();
  };

  const archive = async () => {
    setLoadingFlow(true);

    const saveResponse = await FlowAPI.putArchiveFlow(flow.id);
    if (saveResponse.getErrors([]).length > 0) {
      alert({
        text: saveResponse.getErrors(["Ocorreu um erro ao arquivar"])[0],
        buttonText: "Voltar",
      });
    } else {
      alert({ text: "Arquivado com sucesso!", buttonText: "Ok" });
    }
    const flowResponse = await FlowAPI.getFlowById(flow.id);
    setLoadingFlow(false);
    setFlow(flowResponse.getData(null));
  };

  const pause = async () => {
    setLoadingFlow(true);

    const saveResponse = await FlowAPI.putPauseFlow(flow.id);
    if (saveResponse.getErrors([]).length > 0) {
      alert({
        text: saveResponse.getErrors(["Ocorreu um erro ao pausar"])[0],
        buttonText: "Voltar",
      });
    } else {
      alert({ text: "Pausado com sucesso!", buttonText: "Ok" });
    }
    const flowResponse = await FlowAPI.getFlowById(flow.id);
    setLoadingFlow(false);
    setFlow(flowResponse.getData(null));
  };

  const restoreSteps = async () => {
    const stepsResponse = await FlowAPI.getStepsByFlowId(flow.id);
    const steps = stepsResponse.getData({ boxes: [], links: [] });
    setSteps(steps);
    setSelectedChangeId(steps?.id);
  };

  const proccessFlowRestrictions = ({ boxes, flow }) => {
    let userRole = UserStorage.getLoggedUser()?.role;

    const restrictions = {
      lockAudienceGroup: false,
      lockFullAudience: false,
      lockFilteredAudience: false,
      lockImportedAudience: false,
      lockSavedAudience: false,
      lockBroadcastAudience: false,
      lockEventSource: false,
      lockFilters: false,
      lockConditionals: false,
      lockPushCreativeAction: false,
      lockCreativeBenefit: false,
      lockBeforeCreativeTime: false,
      lockFlowPanel: false,
    };

    boxes.map((box) => {
      if (atLastOneAudience) {
        restrictions.lockAudienceGroup = true;
        restrictions.lockFullAudience = true;
        restrictions.lockFilteredAudience = true;
        restrictions.lockEventSource = true;
        restrictions.lockImportedAudience = true;
        restrictions.lockSavedAudience = true;
        restrictions.lockBroadcastAudience = true;
      }

      if (box.type === "ImportedAudience") {
        restrictions.lockAudienceGroup = true;
        restrictions.lockFullAudience = true;
        restrictions.lockFilteredAudience = true;
        restrictions.lockEventSource = true;
        restrictions.lockFilters = true;
        // restrictions.lockConditionals = true;
        restrictions.lockPushCreativeAction = true;
        restrictions.lockCreativeBenefit = true;
      }

      if (box.type === "SpecificDayConditional") {
        restrictions.lockBeforeCreativeTime = box.data?.use_type == "TODAY";
        restrictions.lockFlowPanel = flow?.state === "PUBLISHED";
      }

      if (
        box.type?.includes("Filter") ||
        box.type?.includes("Conditional") ||
        userRole !== ROLES.SUPPORT
      ) {
        restrictions.lockImportedAudience = true;
      }
    });

    return restrictions;
  };

  const renderDropdownMenu = (pointFlow, isValidFlow) => {
    const closeCallback = (callback) => {
      setCollapsed(false);
      callback && callback();
    };

    return (
      <div className={styles.drop}>
        <div
          className={css({ button: true, active: !openHistoric && collapsed })}
          onClick={() => !openHistoric && setCollapsed(!collapsed)}
        >
          <i className="fas fa-chevron-down" />
        </div>

        <div className={styles.collapsed}>
          {/* <If cond={["DRAFT", "PAUSED"].includes(flow?.state) && FlowBox.isValidFlow(steps?.boxes || [], steps?.links || [])}> */}
          <If cond={["DRAFT", "PAUSED", "PUBLISHED"].includes(flow?.state)}>
            <DropDownItem
              title={
                flow?.state == "PUBLISHED" ? "Publicar novamente" : "Publicar"
              }
              icon={"paper-plane-top"}
              onClick={() => {
                setCollapsed(false);
                setOpenPublishAgain(true);
              }}
              disabled={
                (pointFlow && flow?.state === "PUBLISHED") || !isValidFlow
              }
            />
          </If>
          <If cond={["PUBLISHED"].includes(flow?.state)}>
            <DropDownItem
              title={"Pausar"}
              icon={"pause"}
              onClick={() => closeCallback(() => pause())}
              disabled={!flow?.state === "PUBLISHED"}
            />
          </If>
          <If cond={["PAUSED", "DRAFT"].includes(flow?.state) || pointFlow}>
            <DropDownItem
              title={"Arquivar"}
              icon={"box-archive"}
              onClick={() => closeCallback(() => archive())}
            />
          </If>
          <DropDownItem
            title={"Editar"}
            icon={"pen"}
            onClick={() => {
              setOpenEditFlow(!openEditFlow);
              setCollapsed(false);
            }}
          />
          <If cond={!["DRAFT"].includes(flow?.state)}>
            <DropDownItem
              title={"Histórico"}
              icon={"list-radio"}
              onClick={() => closeCallback(() => loadHistoric())}
              disabled={pointFlow && flow?.state === "PUBLISHED"}
            />
          </If>
        </div>
      </div>
    );
  };

  const getAtLastOneAudience = (updatedSteps) => {
    const audienceTypes = [
      "FullAudience",
      "SavedAudience",
      "ImportedAudience",
      "EventSource",
      "BroadcastAudience",
    ];
    const boxes = updatedSteps?.boxes;

    const isValid = (type, validationKey) => {
      const filteredAudiences = boxes?.filter((a) => a.type === type);
      return filteredAudiences?.every((s) => s.data?.[validationKey]);
    };

    const isValidfullAudiences = isValid("ImportedAudience", "type");
    const isValidSavedAudiences = isValid("SavedAudience", "audienceId");
    const isValidImportedAudiences = isValid("ImportedAudience", "fileName");
    const isValidEventSources = isValid("EventSource", "eventType");

    return (
      isValidSavedAudiences &&
      isValidImportedAudiences &&
      isValidEventSources &&
      isValidfullAudiences &&
      updatedSteps?.boxes?.some((a) => audienceTypes.includes(a.type))
    );
  };

  const getCreatives = (updatedSteps) => {
    const creativeActionTypes = [
      "PushCreativeAction",
      "SmsCreativeAction",
      "EmailCreativeAction",
    ];

    let creatives = [];

    if (updatedSteps) {
      creatives.push(
        updatedSteps?.boxes
          .filter((a) => a.type === "CreativeGroupAction")
          .map((b) =>
            b.data?.creatives
              ?.filter((c) => creativeActionTypes.includes(c.type))
              .map((d) => ({ ...d.data, type: d.type }))
          )
      );

      creatives.push(
        updatedSteps?.boxes
          ?.filter((a) => creativeActionTypes.includes(a.type))
          .map((b) => ({ ...b.data, type: b.type }))
      );
    }

    return creatives;
  };

  const getCreativesWithMessage = (updatedSteps) => {
    const creativesWithMessage = getCreatives(updatedSteps)
      .flat(Infinity)
      .map((a) => {
        const message = a?.creative?.message;
        const hasMessage = message?.template || message?.filename || null;

        return hasMessage;
      })
      .filter((b) => b !== null);

    return creativesWithMessage;
  };

  const getBlockPublish = () => {
    const creatives = getCreatives(updatedSteps).flat(Infinity).map((c) => c.creative);

    const pointFlowToday = updatedSteps?.boxes
      .filter((a) => a.type === "SpecificDayConditional")
      .find((a) => a?.data?.use_type === "TODAY");

    const differenceInHours = creatives.map((creative) => {
      if (!creative) {
        return false;
      }

      // Liberação somente para PushCreativeAction
      if(creative.type == 'PushCreativeAction') {
        return 99;
      }

      const c = creative?.sendTime;
      const now = dayjs();
      const hour = dayjs().hour(c.slice(0, 2));
      const differenceInHours = hour.diff(now, "hour");

      return differenceInHours;
    });


    const differenceGreaterThanTwoHours = differenceInHours.some((d) => d < 2);
    const blockPublish = !!differenceGreaterThanTwoHours && !!pointFlowToday;

    return blockPublish;
  };

  const renderActions = () => {
    // const isValidFlow = openHistoric || FlowBox.isValidFlow(updatedSteps?.boxes || steps?.boxes || [], updatedSteps?.links || steps?.links || []);

    const pointFlow = updatedSteps?.boxes.find(
      (a) => a.type === "SpecificDayConditional"
    );

    const creativesQuantity = getCreatives(updatedSteps)?.flat(Infinity);
    const creativesWithMessage = getCreativesWithMessage(updatedSteps);

    const allCreativesHaveMessage =
      creativesQuantity?.length === creativesWithMessage.length;

    const boxes = updatedSteps?.boxes || steps?.boxes || [];

    const isBroadcastFlow = boxes.some(
      (box) => box.type === BoxTypes.BroadcastAudience
    );
    const hasSpecificClimateConditional = boxes.some(
      (box) => box.type === BoxTypes.SpecificClimateConditional
    );
    const isValidBroadcastFlow = isBroadcastFlow
      ? !hasSpecificClimateConditional
      : true;

    const atLastOneAudience = getAtLastOneAudience(updatedSteps);

    const isValidFlow =
      creativesQuantity?.length > 0 &&
      allCreativesHaveMessage &&
      atLastOneAudience &&
      isValidBroadcastFlow;

    return (
      <FlowHeaderActions
        flow={flow}
        isValidFlow={isValidFlow}
        hasChanges={hasChanges || updatedFlow}
        changesHistoryOpened={openHistoric}
        dropdownMenu={renderDropdownMenu(pointFlow, isValidFlow)}
        processingAudience={processing}
        flowType={FlowBox.isEventFlow(steps?.boxes || []) ? "EVENT" : "COMMON"}
        onReportsClick={() => loadReports()}
        onPreview={() => preview()}
        onSave={() => onSave()}
        onRestoreSteps={() => {
          setOpenHistoric(false);
          restoreSteps();
        }}
        onPublish={async (saveBefore = true) => {
          if (saveBefore) {
            await onSave();
          }
          await publish(flow.type == "RANK_BYPASS");
        }}
        pointFlow={pointFlow && flow?.state === "PUBLISHED"}
      />
    );
  };

  const navigate = useNavigate();

  const pointFlow = updatedSteps?.boxes.find(
    (a) => a.type === "SpecificDayConditional"
  );

  const creativesQuantity = getCreatives(updatedSteps)?.flat(Infinity);
  const creativesWithMessage = getCreativesWithMessage(updatedSteps);
  const blockPublish = getBlockPublish();

  const allCreativesHaveMessage =
    creativesQuantity?.length === creativesWithMessage.length;
  const atLastOneAudience = getAtLastOneAudience(updatedSteps);

  const hasCreativePush = creativesQuantity.find(
    (c) => c.type === "PushCreativeAction"
  );
  const pointFlowBoxes = updatedSteps?.boxes.filter(
    (a) => a.type === "SpecificDayConditional"
  );

  const isToday = (creative) => {
    const today = dayjs();

    if (!creative?.data?.date) return;

    const pointFlowDate = dayjs(
      creative?.data?.date,
      "DD/MM/YYYY",
      "pt-br",
      true
    );

    return pointFlowDate.isSame(today, "day");
  };

  const pointFlowToday = pointFlowBoxes?.map((p) => {
    return isToday(p);
  });

  const hasAnyPointFlowToday = pointFlowToday?.find((p) => !!p);

  const isCocoBambu = Environment.id === 49;
  const blockPublishCreativePush = false && !!hasCreativePush && hasAnyPointFlowToday && !isCocoBambu;

  const isValidFlow =
    creativesQuantity?.length > 0 &&
    allCreativesHaveMessage &&
    atLastOneAudience;

  return (
    <>
      <If cond={loadingFlow}>
        <Loading fullContent={true} />
      </If>

      <div className={styles.headerFlow}>
        <div className={styles.flowState}>
          <span>{FlowBox.getFlowStatusName(flow?.state, flow?.type)}</span>
        </div>
        <div className={styles.flowDetails}>
          <div
            className={styles.flowName}
            onClick={() => {
              console.log('flow', flow);
              if (flow?.folder) navigate(`/fluxos/pasta/${flow?.folder}`);
              else if (flow?.state === "ARCHIVED") navigate(`/fluxos/arquivados`);
              else navigate(`/fluxos`);
            }}
          >
            <i className="far fa-chevron-left" />
            <p>
              Meus fluxos<b>/</b>
              <span>{flow && flow.name}</span>
            </p>
          </div>
          <div className={styles.updatedAt}>
            Última alteração em:&nbsp;
            <span>
              {dayjs
                .utc(flow?.updated_at)
                .tz("America/Sao_Paulo")
                .format("DD/MM/YYYY")}
            </span>
            &nbsp;às&nbsp;
            <span>
              {dayjs
                .utc(flow?.updated_at)
                .tz("America/Sao_Paulo")
                .format("HH:mm")}
            </span>
          </div>
        </div>

        {renderActions()}
      </div>

      <Flow
        key="flow-playground"
        stepId={steps?.id}
        flow={flow}
        steps={steps}
        locked={pointFlow && flow?.state === "PUBLISHED"}
        onChange={async ({ boxes, links }) => {
          const updatedSteps = {
            boxes: boxes,
            links: links,
          };

          boxes.forEach((obj) => {
            delete obj.width;
            delete obj.height;
          });

          const currentHash = hash({ boxes, links });
          const previousHash = hash({
            boxes: steps?.boxes || [],
            links: steps?.links || [],
          });

          setHasChanges(currentHash !== previousHash);

          setUpdatedSteps(updatedSteps);

          if (flow?.id) {
            FlowStorage.setFlowType(
              flow.id,
              FlowBox.isEventFlow(boxes || []) ? "EVENT" : "COMMON"
            );
            setFlow({
              ...flow,
              restrictions: proccessFlowRestrictions({ boxes, flow }),
            });
            onChange && onChange(updatedSteps);
          }
        }}
      />
      <FlowReportDrawer
        flowId={flow?.id}
        stepId={steps?.id}
        visible={openReports}
        reports={reports}
        globalReport={!openHistoric}
        onClose={() => {
          setOpenReports(false);
          setReports(null);
        }}
      />
      <FlowUpdateDrawer
        flow={flow}
        folders={folders}
        visible={openEditFlow}
        onClose={() => setOpenEditFlow(false)}
        onSave={({ name, folder, rank_bypass }) => {
          const newFlow = flow;
          newFlow.name = name;
          newFlow.folder = folder;
          newFlow.type = rank_bypass ? "RANK_BYPASS" : "COMMON";

          setFlow(newFlow);
          setOpenEditFlow(!openEditFlow);
          setUpdatedFlow(true);
        }}
      />
      <ChangeHistorySidebar
        visible={openHistoric}
        selectedChange={selectedChangeId}
        onSelectChange={(moment) => selectChangeMoment(moment)}
        changes={changes}
      />

      <Drawer
        title="Publicar"
        direction="top-alert"
        visible={openPublishAgain}
        onClose={() => setOpenPublishAgain(false)}
      >
        <div className="d-flex flex-column align-center">
          {!blockPublish && <Label>Deseja publicar o fluxo?</Label>}
          {flow?.state === "PUBLISHED" && (
            <Label>
              Atenção: ao publicar o fluxo, todos os relatórios serão zerados.
            </Label>
          )}
          {blockPublishCreativePush && !blockPublish && (
            <Message
              type="danger"
              text="Não é permitido realizar disparos de push para o dia corrente. Para publicar, altere o dia especifico para um dia futuro."
            />
          )}
          {!!pointFlow && !blockPublish && !blockPublishCreativePush && (
            <Message
              type="danger"
              text="Atenção! Ao publicar este fluxo, ele não será mais editável, confirme todas as configurações."
            />
          )}
          {!!blockPublish && (
            <Message
              type="danger"
              text="Não é possível publicar este fluxo, pois o horário do disparo está configurado para menos de 2 horas do horário atual."
            />
          )}
          <div className="mt-20 d-flex align-center w-100">
            <Button
              className="w-100"
              onClick={() => setOpenPublishAgain(false)}
              design="default-2"
            >
              Cancelar
            </Button>
            <Button
              className="w-100 ml-15"
              onClick={() => publish(true)}
              disabled={
                !!blockPublish || !isValidFlow || !!blockPublishCreativePush
              }
              design="primary"
            >
              Sim, publicar
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default () => {
  const [hasPageChange, setHasPageChange] = useState(false);

  // useLeavePageConfirmation(hasPageChange, {
  //   text: "Existem alterações que não foram salvas. Você realmente deseja sair?",
  //   confirmText: "Sair",
  //   cancelText: "Cancelar",
  // });

  return <FlowDetails onChange={(steps) => setHasPageChange(!!steps)} />;
};
